import { useState, useCallback, useEffect, useRef } from 'react';
import type { Message } from '~/lib/api';
import { useWebSocketContext } from '~/contexts/WebSocketContext';
import type { WSEvent } from './useWebSocket';

interface UseMessageStreamOptions {
  onMessage?: (message: Message) => void;
  enabled?: boolean;
}

export function useMessageStream(options: UseMessageStreamOptions = {}) {
  const { onMessage, enabled = true } = options;
  const [messages, setMessages] = useState<Message[]>([]);
  const onMessageRef = useRef(onMessage);
  onMessageRef.current = onMessage;

  const { connected, error, reconnecting, subscribe } = useWebSocketContext();

  const handleEvent = useCallback((event: WSEvent) => {
    if (event.type === 'message') {
      const data = event.data as unknown as Message;

      setMessages((prev) => {
        if (prev.some((m) => m.id === data.id)) return prev;
        return [data, ...prev].slice(0, 100); // Keep last 100
      });

      onMessageRef.current?.(data);
      return;
    }

    if (event.type === 'message_status_updated') {
      const { id, processing_status } = event.data as { id: number; processing_status: string };
      setMessages((prev) => {
        const idx = prev.findIndex((m) => m.id === id);
        if (idx === -1) return prev;
        const updated = [...prev];
        updated[idx] = { ...updated[idx], processing_status };
        return updated;
      });
      return;
    }
  }, []);

  useEffect(() => {
    if (!enabled) return;
    return subscribe(handleEvent);
  }, [enabled, subscribe, handleEvent]);

  const clearMessages = useCallback(() => {
    setMessages([]);
  }, []);

  return {
    connected,
    error,
    reconnecting,
    messages,
    clearMessages,
  };
}
